import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('min_price') || '',
    maxPrice: searchParams.get('max_price') || '',
    isNew: searchParams.get('is_new') === 'true',
    isFeatured: searchParams.get('is_featured') === 'true',
    search: searchParams.get('search') || ''
  });

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('category')) params.append('category', searchParams.get('category'));
      if (searchParams.get('min_price')) params.append('min_price', searchParams.get('min_price'));
      if (searchParams.get('max_price')) params.append('max_price', searchParams.get('max_price'));
      if (searchParams.get('is_new')) params.append('is_new', searchParams.get('is_new'));
      if (searchParams.get('is_featured')) params.append('is_featured', searchParams.get('is_featured'));
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.minPrice) params.set('min_price', filters.minPrice);
    if (filters.maxPrice) params.set('max_price', filters.maxPrice);
    if (filters.isNew) params.set('is_new', 'true');
    if (filters.isFeatured) params.set('is_featured', 'true');
    if (filters.search) params.set('search', filters.search);
    setSearchParams(params);
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      minPrice: '',
      maxPrice: '',
      isNew: false,
      isFeatured: false,
      search: ''
    });
    setSearchParams({});
  };

  const activeFiltersCount = [
    filters.category,
    filters.minPrice,
    filters.maxPrice,
    filters.isNew,
    filters.isFeatured,
    filters.search
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="py-12 border-b border-black/10 mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-6xl tracking-tight"
            data-testid="shop-title"
          >
            {searchParams.get('is_new') ? 'Nouveautés' : 
             searchParams.get('is_featured') ? 'Collection' : 
             searchParams.get('search') ? `Résultats pour "${searchParams.get('search')}"` :
             'Boutique'}
          </motion.h1>
          <p className="text-foreground/60 mt-4">
            {products.length} produit{products.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button
            onClick={() => setIsFilterOpen(true)}
            className="btn-secondary flex items-center gap-2"
            data-testid="open-filters-btn"
          >
            <SlidersHorizontal size={16} />
            Filtres
            {activeFiltersCount > 0 && (
              <span className="ml-2 w-5 h-5 bg-gold text-black text-xs flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {activeFiltersCount > 0 && (
            <button
              onClick={clearFilters}
              className="text-sm text-foreground/60 hover:text-gold transition-colors flex items-center gap-1"
              data-testid="clear-filters-btn"
            >
              <X size={14} />
              Effacer les filtres
            </button>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-[3/4] bg-black/5 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-foreground/60 text-lg">Aucun produit trouvé</p>
            <button onClick={clearFilters} className="btn-primary mt-6">
              Voir tous les produits
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8" data-testid="products-grid">
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        )}
      </div>

      {/* Filter Drawer */}
      <AnimatePresence>
        {isFilterOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50"
              onClick={() => setIsFilterOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-background z-50 overflow-y-auto border-l border-black/10"
              data-testid="filter-drawer"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-heading text-2xl">Filtres</h2>
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="p-2 hover:text-gold transition-colors"
                    data-testid="close-filters-btn"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Category Filter */}
                <div className="mb-8">
                  <h3 className="text-sm uppercase tracking-widest text-gold mb-4">Catégorie</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setFilters({ ...filters, category: '' })}
                      className={`block w-full text-left py-2 px-3 transition-colors ${
                        !filters.category ? 'text-gold border-l-2 border-gold' : 'text-foreground/60 hover:text-foreground'
                      }`}
                      data-testid="filter-category-all"
                    >
                      Toutes les catégories
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setFilters({ ...filters, category: cat })}
                        className={`block w-full text-left py-2 px-3 transition-colors ${
                          filters.category === cat ? 'text-gold border-l-2 border-gold' : 'text-foreground/60 hover:text-foreground'
                        }`}
                        data-testid={`filter-category-${cat.toLowerCase()}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Filter */}
                <div className="mb-8">
                  <h3 className="text-sm uppercase tracking-widest text-gold mb-4">Prix</h3>
                  <div className="flex gap-4">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                      className="w-full bg-transparent border border-black/20 py-2 px-3 focus:border-gold focus:outline-none"
                      data-testid="filter-min-price"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                      className="w-full bg-transparent border border-black/20 py-2 px-3 focus:border-gold focus:outline-none"
                      data-testid="filter-max-price"
                    />
                  </div>
                </div>

                {/* Quick Filters */}
                <div className="mb-8">
                  <h3 className="text-sm uppercase tracking-widest text-gold mb-4">Filtres rapides</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.isNew}
                        onChange={(e) => setFilters({ ...filters, isNew: e.target.checked })}
                        className="w-4 h-4 accent-gold"
                        data-testid="filter-is-new"
                      />
                      <span className="text-foreground/80">Nouveautés uniquement</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.isFeatured}
                        onChange={(e) => setFilters({ ...filters, isFeatured: e.target.checked })}
                        className="w-4 h-4 accent-gold"
                        data-testid="filter-is-featured"
                      />
                      <span className="text-foreground/80">Collection mise en avant</span>
                    </label>
                  </div>
                </div>

                {/* Apply Button */}
                <div className="flex gap-4">
                  <button
                    onClick={clearFilters}
                    className="btn-secondary flex-1"
                    data-testid="filter-clear-btn"
                  >
                    Effacer
                  </button>
                  <button
                    onClick={applyFilters}
                    className="btn-primary flex-1"
                    data-testid="filter-apply-btn"
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShopPage;
